import { useEffect, useRef, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Incident } from '../../types';
import { useAppSelector } from '../../store';

// Fix default marker icons
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
});

const severityColors: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e', info: '#3b82f6',
};

function createIncidentIcon(severity: string) {
  const color = severityColors[severity] || '#64748b';
  return L.divIcon({
    className: 'custom-incident-marker',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

interface SerializedDrawing {
  id: string;
  type: 'marker' | 'circle' | 'polyline' | 'polygon';
  label?: string;
  notes?: string;
  color?: string;
  latlng?: { lat: number; lng: number };
  latlngs?: Array<{ lat: number; lng: number }> | Array<Array<{ lat: number; lng: number }>>;
  radius?: number;
}

interface MobileMapWithDrawingsProps {
  className?: string;
  incidents?: Incident[];
  center?: [number, number];
  zoom?: number;
  drawings?: SerializedDrawing[];
  onIncidentClick?: (incident: Incident) => void;
}

export default function MobileMapWithDrawings({
  className,
  incidents = [],
  center = [51.505, -0.09],
  zoom = 13,
  drawings = [],
  onIncidentClick,
}: MobileMapWithDrawingsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup>(L.layerGroup());
  const drawingsRef = useRef<L.LayerGroup>(L.layerGroup());
  const emergencyLocation = useAppSelector((state) => state.ui.emergencyLocation);
  const emergencyMarkerRef = useRef<L.Marker | null>(null);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center,
      zoom,
      zoomControl: false,
      attributionControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '',
      maxZoom: 19,
    }).addTo(map);

    markersRef.current.addTo(map);
    drawingsRef.current.addTo(map);
    mapRef.current = map;

    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update center/zoom when props change
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setView(center, zoom);
  }, [center, zoom]);

  // Update incident markers
  useEffect(() => {
    if (!mapRef.current) return;
    
    markersRef.current.clearLayers();
    
    incidents.forEach((incident) => {
      if (!incident.location?.latitude || !incident.location?.longitude) return;
      
      const marker = L.marker(
        [incident.location.latitude, incident.location.longitude],
        { icon: createIncidentIcon(incident.severity) }
      );
      
      marker.bindPopup(`
        <div style="font-family: system-ui; font-size: 12px;">
          <strong>${incident.id}</strong><br/>
          ${incident.title}<br/>
          <span style="color: #666; font-size: 10px;">${incident.location.address || ''}</span>
        </div>
      `);
      
      if (onIncidentClick) {
        marker.on('click', () => onIncidentClick(incident));
      }
      
      marker.addTo(markersRef.current);
    });
  }, [incidents, onIncidentClick]);

  // Render drawings
  useEffect(() => {
    if (!mapRef.current) return;
    
    drawingsRef.current.clearLayers();
    
    drawings.forEach((drawing) => {
      try {
        if (drawing.type === 'marker' && drawing.latlng) {
          const marker = L.marker([drawing.latlng.lat, drawing.latlng.lng]);
          if (drawing.label || drawing.notes) {
            marker.bindPopup(`
              <div style="font-family: system-ui; font-size: 11px;">
                ${drawing.label ? `<strong>${drawing.label}</strong><br/>` : ''}
                ${drawing.notes || ''}
              </div>
            `);
          }
          marker.addTo(drawingsRef.current);
          
        } else if (drawing.type === 'circle' && drawing.latlng && drawing.radius) {
          const circle = L.circle([drawing.latlng.lat, drawing.latlng.lng], {
            radius: drawing.radius,
            color: drawing.color || '#2563eb',
            fillColor: drawing.color || '#2563eb',
            fillOpacity: 0.2,
          });
          circle.addTo(drawingsRef.current);
          
        } else if (drawing.type === 'polyline' && drawing.latlngs && Array.isArray(drawing.latlngs)) {
          const latlngs = drawing.latlngs
            .filter((ll: any) => ll && typeof ll.lat === 'number' && typeof ll.lng === 'number')
            .map((ll: any) => [ll.lat, ll.lng]);
          if (latlngs.length > 1) {
            const polyline = L.polyline(latlngs as L.LatLngExpression[], {
              color: drawing.color || '#f97316',
            });
            polyline.addTo(drawingsRef.current);
          }
          
        } else if (drawing.type === 'polygon' && drawing.latlngs && Array.isArray(drawing.latlngs)) {
          // Handle nested array for polygons with proper validation
          try {
            const latlngs = drawing.latlngs
              .filter((ring: any) => Array.isArray(ring) && ring.length > 0)
              .map((ring: any) => 
                ring
                  .filter((ll: any) => ll && typeof ll.lat === 'number' && typeof ll.lng === 'number')
                  .map((ll: any) => [ll.lat, ll.lng])
              )
              .filter((ring: number[][]) => ring.length > 0);
            
            if (latlngs.length > 0 && latlngs[0].length > 0) {
              const polygon = L.polygon(latlngs as L.LatLngExpression[][], {
                color: drawing.color || '#7c3aed',
                fillColor: drawing.color || '#7c3aed',
                fillOpacity: 0.2,
              });
              polygon.addTo(drawingsRef.current);
            }
          } catch (e) {
            console.warn('Skipping invalid polygon:', e);
          }
        }
      } catch (error) {
        console.error('Error rendering drawing:', error);
      }
    });
  }, [drawings]);

  // Emergency location marker
  useEffect(() => {
    if (!mapRef.current || !emergencyLocation) return;
    
    if (emergencyMarkerRef.current) {
      emergencyMarkerRef.current.remove();
    }
    
    const marker = L.marker(
      [emergencyLocation.latitude, emergencyLocation.longitude],
      {
        icon: L.divIcon({
          className: 'emergency-marker',
          html: '<div style="width:20px;height:20px;border-radius:50%;background:#ef4444;border:3px solid #fff;box-shadow:0 0 15px #ef4444;animation:pulse 1s infinite;"></div>',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      }
    );
    
    marker.bindPopup('<strong>URGENT ASSISTANCE</strong><br/>Officer needs help!');
    marker.addTo(mapRef.current);
    emergencyMarkerRef.current = marker;
    
    mapRef.current.setView(
      [emergencyLocation.latitude, emergencyLocation.longitude],
      16
    );
  }, [emergencyLocation]);

  return (
    <div 
      ref={containerRef} 
      className={`h-full w-full ${className || ''}`}
      style={{ minHeight: '100%' }}
    />
  );
}
